// IMP-438: 현재 앱 언어 코드 해석 + BCP-47 locale tag SSoT.
//
// 왜 Translate.js 가 아니라 별도 모듈인가 —
//   이 해석 로직은 원래 Translate.js 의 getCurrentLangFile() 안에 있었고, IMP-438 은 처음에
//   Translate.js 에서 getCurrentLocaleTag 를 export 하는 방식을 택했다. 그런데 이 저장소에는
//   `jest.mock('.../config/Translate', () => ({ Translate: (k) => k }))` 로 **Translate 모듈을
//   통째로 수동 모킹하는 테스트가 172개** 있다. 날짜 포맷이 Translate 모듈에 의존하면 그 172개
//   전부에서 getCurrentLocaleTag 가 undefined 가 되어, 포맷 함수의 try/catch 가 이를 삼키고
//   조용히 '-' 를 반환한다(실제로 CustomerLicensesTable.test.js 2건이 이 경로로 깨졌다).
//   즉 "테스트가 날짜 렌더를 검증하지 못하는" 상태가 광범위하게 생긴다.
//   해석 로직을 이 모듈로 분리하면 Translate 모킹과 날짜 포맷이 서로 독립이 된다.
//
// 정의는 여기 한 곳뿐이다 — Translate.js 는 이 모듈의 resolveCurrentLangCode() 를 import 해
// 쓴다(로직 복제 금지, IMP-438 완료 기준).

import { toBcp47 } from './langResources';
import { getPreLoginLang, normalizeLangCode } from '../lib/preLoginLang';

// 비어 있지 않은데 지원 목록 밖인 코드는 코드당 1회만 경고한다(렌더마다 호출되므로).
const warnedUnsupported = new Set();

const pickSupported = (field, raw) => {
    const code = normalizeLangCode(raw);
    if (!code && raw && !warnedUnsupported.has(raw)) {
        warnedUnsupported.add(raw);
        console.warn('[currentLang] unsupported user lang code, skipped', { field, code: raw });
    }
    return code;
};

// 사용자 계정에 저장된 언어(다른 사람 계정을 보여줄 때도 쓴다 — 로그인 때 고른 언어는 쓰지 않는다):
//   rguardSettingLanguage → language (둘 다 옛 코드 정규화 후 지원 19개 안일 때만), 없으면 null
export const resolveStoredUserLangCode = (user) =>
    pickSupported('rguardSettingLanguage', user?.rguardSettingLanguage) ||
    pickSupported('language', user?.language) ||
    null;

// 사용자 계정의 이메일·보고서 언어. 비어 있으면 서버도 영어(ENG)로 보내므로 화면에도 'eng' 로 보인다.
export const resolveStoredMailLangCode = (user) => user?.rguardSettingEmailReportLanguage || 'eng';

// 사용자 언어 해석 규칙(화면 문구·DB 번역·날짜 선택 달력·부트가 함께 쓴다):
//   resolveStoredUserLangCode(user) → getPreLoginLang() (로그인 때 고른 언어, 항상 지원 코드)
export const resolveUserLangCode = (user) =>
    resolveStoredUserLangCode(user) || getPreLoginLang();

// 현재 앱 언어 코드:
//   localStorage.user 존재 → resolveUserLangCode(user)
//   user 없음 → getPreLoginLang() (IMP-001: 로그아웃 상태의 로그인 전 언어 선호도)
//   JSON.parse 실패 등 예외 → null (호출부가 영어 fallback 처리)
export const resolveCurrentLangCode = () => {
    try {
        const user = localStorage.getItem('user');
        if (user) {
            return resolveUserLangCode(JSON.parse(user));
        }
        return getPreLoginLang();
    } catch (e) {
        // 프리픽스에 Translate 를 남긴다 — 이 로직은 Translate.js 에서 옮겨온 것이고,
        // 기존 로그 검색/알람 쿼리가 '[Translate]' 를 키로 잡고 있어 연속성이 필요하다.
        console.warn('[Translate/currentLang] Failed to parse user language:', e);
        return null;
    }
};

// 현재 앱 언어의 BCP-47 locale tag. toLocaleString()/toLocaleDateString() 호출부가
// langResources 를 따로 import 하지 않도록 여기서 조립한다.
// localStorage.user JSON 파싱 실패로 resolveCurrentLangCode() 가 null 을 반환해도 toBcp47() 이 'en' 으로 처리한다.
export const getCurrentLocaleTag = () => toBcp47(resolveCurrentLangCode());
